// What both cloud's server and its screen need about Workspaces: names, limits
// and the shapes the REST answers have. No dependencies, so the screen
// (private/apps/web) imports it as `@trameria/cloud-api/workspace-shared`
// without pulling server code in.
//
// Promise:
// - A slug is 3 to 32 lowercase letters, digits or '-', starting and ending
//   with a letter or digit, and not one of RESERVED_SLUGS (the screen's
//   top-level paths).
// - Permission and feature names are registered by the parts that own them;
//   the names here are the Workspace part's own. A stored name nobody
//   registered is kept but grants nothing.
// - Answers never say whether something hidden exists: a workspace one is not
//   in, or a space one cannot see, is answered like one that is not there.

export const WORKSPACE_NAME_MAX = 50;
export const DISPLAY_NAME_MAX = 50;
export const ROLE_NAME_MAX = 40;
export const SPACE_NAME_MAX = 50;
export const SPACE_DESCRIPTION_MAX = 1000;
export const MEMBER_LIST_MAX = 200;

export const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{1,30}[a-z0-9]$/;

/** A space's slug: 1 to 40 lowercase letters, digits or '-', starting and ending with a letter or digit. */
export const SPACE_SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/;

/** Top-level paths of the screen; a workspace may not take them. */
export const RESERVED_SLUGS = [
  "me",
  "new",
  "invite",
  "signin",
  "waitlist",
  "passkey",
  "oauth",
  "admin",
  "settings",
  "api",
  "assets",
  "help",
  "login",
  "preview",
  "preview-auth",
  "previews",
  "team",
  "w",
  "well-known",
] as const;

export function slugProblem(slug: string): "format" | "reserved" | null {
  if (!SLUG_PATTERN.test(slug)) return "format";
  if ((RESERVED_SLUGS as readonly string[]).includes(slug)) return "reserved";
  return null;
}

export const MEMBER_KINDS = ["member", "guest"] as const;
export type MemberKind = (typeof MEMBER_KINDS)[number];

/** anyone = everyone in the workspace, members = Members only, private = its members only. */
export const SPACE_VISIBILITIES = ["anyone", "members", "private"] as const;
export type SpaceVisibility = (typeof SPACE_VISIBILITIES)[number];

/** Where a favorite or a link of a workspace lives (core's `place`). */
export const workspacePlace = (workspaceId: string) => `w:${workspaceId}`;
export const placeWorkspaceId = (place: string): string | null =>
  place.startsWith("w:") ? place.slice(2) : null;

// ---- answers ------------------------------------------------------------------

/** GET /api/workspaces → { workspaces }, in the order one joined them. */
export interface WorkspaceSummary {
  id: string;
  slug: string;
  name: string;
  /** The icon's file (`/api/files/<id>`); null = the first letter on a ground. */
  iconFileId: string | null;
  joinedAt: string;
}

/** A permission or feature name with what to show for it. */
export interface NamedFlag {
  name: string;
  /** Workspace or space (features only). */
  scope?: "workspace" | "space";
  /** For features: another feature this one needs (cycle needs issue). */
  requires?: string;
}

/** GET /api/w/:slug: the workspace as the person sees it. */
export interface WorkspaceView extends WorkspaceSummary {
  ownerUserId: string;
  me: {
    userId: string;
    kind: MemberKind;
    isOwner: boolean;
    /** Effective permissions (registered names only). */
    permissions: string[];
    roleIds: string[];
    /** Sees every space, private ones too. */
    canSeeAll: boolean;
    displayName: string;
    /** The picture shown for the person here: this workspace's, else the account's. */
    avatarFileId: string | null;
    /** The picture set for this workspace only (null = the account's shows). */
    workspaceAvatarFileId: string | null;
  };
  /** Workspace features that are on. */
  features: string[];
  mcpAllowed: boolean;
  publishAllowed: boolean;
  /** What the parts registered, for the settings screens. */
  available: { permissions: NamedFlag[]; features: NamedFlag[] };
}

/** GET /api/w/:slug/spaces → { spaces }, in the person's order. */
export interface SpaceView {
  id: string;
  workspaceId: string;
  slug: string;
  name: string;
  description: string;
  visibility: SpaceVisibility;
  /** Space features that are on. */
  features: string[];
  archivedAt: string | null;
  /** The person is in its members. */
  joined: boolean;
  /** The person may change its settings (a manager, or workspace.manage). */
  canManage: boolean;
}

/** GET /api/w/:slug/members → { members }. */
export interface MemberView {
  userId: string;
  displayName: string;
  /** The picture shown: this workspace's, else the account's. */
  avatarFileId: string | null;
  kind: MemberKind;
  isOwner: boolean;
  roleIds: string[];
  joinedAt: string;
  /** When their trameria account was made; null unless the viewer manages members (member.manage or workspace.manage). */
  registeredAt: string | null;
  /** Their latest sign-in session's last refresh; null when they have none, or unless the viewer manages members. */
  lastActiveAt: string | null;
  /** With ?spaceId=: whether the member can see that space. */
  canSeeSpace?: boolean;
}

/** GET /api/w/:slug/roles → { roles }, highest first. */
export interface RoleView {
  id: string;
  name: string;
  color: string | null;
  position: number;
  permissions: string[];
  isDefault: boolean;
  memberCount: number;
}

/** GET /api/w/:slug/invites → { invites }, newest first. */
export interface InviteView {
  id: string;
  /**
   * The link's token; null in the list for an invite whose role one could not
   * give oneself (only its maker and the owner read that link).
   */
  token: string | null;
  roleId: string | null;
  memberKind: MemberKind;
  maxUses: number | null;
  usedCount: number;
  expiresAt: string | null;
  revokedAt: string | null;
  createdAt: string;
  createdByUserId: string | null;
  /** Not revoked, not expired, not used up. */
  valid: boolean;
}

/** GET /api/invites/:token (no sign-in needed). */
export interface InvitePublicView {
  workspace: { name: string; slug: string };
  memberKind: MemberKind;
  valid: boolean;
}

/** GET /api/w/:slug/spaces/:id/members and /managers → { members }. */
export interface SpaceMemberView {
  userId: string;
  displayName: string;
  avatarFileId: string | null;
  kind: MemberKind;
}

/** GET /api/team/users?q= → { users }: people one shares a workspace with. */
export interface TeamUserView {
  userId: string;
  name: string;
}

/**
 * GET /api/me/mcp → `workspaces`: the person's workspaces and whether outside
 * apps (OAuth MCP) reach them. They do when both `allowed` (the person's grant,
 * PUT /api/me/mcp/workspaces/:slug) and `mcpAllowed` (the workspace's setting)
 * hold. Agents on the person's devices reach every workspace regardless.
 */
export interface McpWorkspaceGrantView {
  slug: string;
  name: string;
  allowed: boolean;
  mcpAllowed: boolean;
}

/**
 * One message of GET /api/team/events (SSE). `cursor` is absent on live
 * notices (not stored, not replayed).
 */
export interface TeamEventView {
  cursor?: string;
  workspaceId: string;
  spaceId: string | null;
  type: string;
  payload: Record<string, unknown>;
  at: string;
}

/** This part's team event types and their payloads. */
export interface WorkspaceTeamEvents {
  "workspace.updated": { workspaceId: string };
  "member.joined": { userId: string };
  "member.updated": { userId: string };
  "member.left": { userId: string };
  "role.changed": { roleId: string };
  "invite.changed": { inviteId: string };
  "space.created": { spaceId: string };
  "space.updated": { spaceId: string };
  "space.deleted": { spaceId: string };
  "space.members_changed": { spaceId: string };
  /** Only to the person who reordered. */
  "space.order_changed": Record<string, never>;
}
